'use client';

import { createDiagnosedResizeObserver } from '@/lib/resize-observer-diagnostics';
import { createAnimationFrameTask } from '@/lib/element-resize';
import { StudioAction } from '@/components/ui/StudioAction';

import { SystemLoadingIcon, SystemLoadingSwitch } from '@/components/ui/SystemLoading';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrandIcon } from '@/components/common/BrandIcon';
import Link from 'next/link';
import Script from 'next/script';
import { useTranslations } from 'next-intl';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, Eye, EyeOff, Gift, KeyRound, Lock, Mail, QrCode, RefreshCw, Shield, X } from 'lucide-react';

import { AuthReferralBanner } from '@/components/referrals/AuthReferralBanner';
import { AppleLoginButton, GoogleLoginButton } from '@/components/auth/GoogleLoginButton';
import { LoginRewardBanner } from '@/components/credits/LoginRewardBanner';
import { LoginImagePanel } from '@/components/auth/LoginImagePanel';
import formStyles from '@/components/auth/LoginForm.module.css';
import {
  buildAuthReferralPayload,
} from '@/lib/referrals/attribution';
import { useAuthStore, type WechatQrSessionStatusData } from '@/stores/authStore';
import type { StoredReferralAttribution } from '@/types/referrals';
import { rememberReferralClaim } from '@/lib/referrals/claim-receipt';
import { getUserFacingErrorMessage } from '@/lib/errors/user-facing-error';
import { API_BASE_URL } from '@/lib/api/base-url';
import { loadGoogleIdentityScript } from '@/lib/auth/googleIdentityLoader';
import { acquireBodyScrollLock } from '@/lib/hooks/useBodyScrollLock';

const WECHAT_QR_POLL_INTERVAL_MS = 5000;
const WECHAT_QR_INITIAL_REVEAL_MS = 1200;
const WECHAT_QR_SLOW_LOAD_MS = 8000;
const WECHAT_QR_CALLBACK_MESSAGE_TYPE = 'dobi:wechat-oauth-callback';
const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID?.trim() || '';
const APPLE_CLIENT_ID = process.env.NEXT_PUBLIC_APPLE_CLIENT_ID?.trim() || '';
const APPLE_REDIRECT_URI = process.env.NEXT_PUBLIC_APPLE_REDIRECT_URI?.trim() || '';
const SHOW_REFERRAL_CONTROLS = true;
interface WechatQrCallbackMessage {
  type: typeof WECHAT_QR_CALLBACK_MESSAGE_TYPE;
  state: string;
  status?: string;
}

interface GoogleCredentialResponse {
  credential?: string;
  select_by?: string;
}

interface GoogleButtonConfiguration {
  type: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black' | 'outline_dark';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
}

interface AppleSignInResponse {
  authorization?: {
    code?: string;
    id_token?: string;
    state?: string;
  };
  user?: {
    email?: string;
    name?: {
      firstName?: string;
      lastName?: string;
    };
  };
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (config: {
            client_id: string;
            callback: (response: GoogleCredentialResponse) => void;
            ux_mode?: 'popup' | 'redirect';
            use_fedcm_for_button?: boolean;
            button_auto_select?: boolean;
          }) => void;
          renderButton: (parent: HTMLElement, options: GoogleButtonConfiguration) => void;
        };
      };
    };
    AppleID?: {
      auth?: {
        init: (config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          state?: string;
          nonce?: string;
          usePopup: boolean;
        }) => void;
        signIn: () => Promise<AppleSignInResponse>;
      };
    };
  }
}

function isWechatQrCallbackMessage(value: unknown): value is WechatQrCallbackMessage {
  return (
    typeof value === 'object'
    && value !== null
    && 'type' in value
    && 'state' in value
    && (value as { type?: unknown }).type === WECHAT_QR_CALLBACK_MESSAGE_TYPE
    && typeof (value as { state?: unknown }).state === 'string'
  );
}

function buildWechatQrFrameUrl(qrUrl: string): string {
  const url = new URL(qrUrl);
  url.searchParams.set('login_type', 'jssdk');
  url.searchParams.set('self_redirect', 'true');
  url.searchParams.set('style', 'black');
  url.searchParams.set('href', `${window.location.origin}/wechat-qrconnect.css`);
  return url.toString();
}

function isTrustedWechatCallbackOrigin(origin: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  if (origin === window.location.origin) {
    return true;
  }

  try {
    return origin === new URL(API_BASE_URL).origin;
  } catch {
    return false;
  }
}

interface LoginDialogProps {
  open: boolean;
  onClose: () => void;
  onAuthenticated: () => void;
  initialMethod?: LoginMethod;
  storedAttribution: StoredReferralAttribution | null;
  wechatQrLoginEnabled: boolean;
}

export type LoginMethod = 'email' | 'wechat' | 'apple';
type LoginEntryMode = 'choices' | 'email';
type EmailLoginMode = 'code' | 'password';
type WechatPanelStatus = 'idle' | 'booting' | 'pending' | 'authorized' | 'email-required' | 'completed' | 'failed' | 'expired';

interface ForgotPasswordPanelProps {
  initialEmail: string;
  onBack: (email?: string) => void;
}

function ForgotPasswordPanel({ initialEmail, onBack }: ForgotPasswordPanelProps) {
  const sendVerificationCode = useAuthStore((state) => state.sendVerificationCode);
  const resetPasswordWithCode = useAuthStore((state) => state.resetPasswordWithCode);
  const t = useTranslations('auth');
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'email' | 'reset'>('email');
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    panelRef.current?.querySelector<HTMLElement>(isSuccess ? 'button' : 'input')?.focus({ preventScroll: true });
  }, [step, isSuccess]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = window.setTimeout(() => setCountdown((current) => current - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  const handleSendCode = async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail || isSendingCode || countdown > 0) return;

    setIsSendingCode(true);
    setError('');
    try {
      await sendVerificationCode(normalizedEmail, 'reset_password');
      setEmail(normalizedEmail);
      setCodeSent(true);
      setCountdown(60);
      setStep('reset');
    } catch (err) {
      setError(getUserFacingErrorMessage(err, 'auth', t('sendVerificationCodeFailed')));
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleReset = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (code.length !== 6) {
      setError(t('enterVerificationCode'));
      return;
    }
    if (newPassword.length < 6) {
      setError(t('passwordTooShort'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('passwordsDoNotMatch'));
      return;
    }

    setIsLoading(true);
    try {
      await resetPasswordWithCode(email.trim(), code, newPassword);
      setIsSuccess(true);
    } catch (err) {
      setError(getUserFacingErrorMessage(err, 'auth', t('resetWithCodeFailed')));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div ref={panelRef} id="forgot-password-dialog-title" className="mt-8">
      {isSuccess ? (
        <div className="space-y-6 py-3 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-[#BAE6FD] bg-[#F0F9FF] text-[#0284C7]">
            <CheckCircle2 size={32} />
          </div>
          <div className="space-y-2">
            <h2 className="text-[24px] font-semibold tracking-[-0.03em] text-[#17191C]">{t('passwordResetSuccess')}</h2>
            <p className="text-[14px] leading-6 text-[#6B7280]">{t('passwordResetSuccessMessage')}</p>
          </div>
          <StudioAction
            type="button"
            onClick={() => onBack(email)}
            data-auth-primary
            className={`${formStyles.action} w-full gap-2`}
           variant="primary">
            <ArrowLeft size={16} />
            {t('backToSignIn')}
          </StudioAction>
        </div>
      ) : step === 'email' ? (
        <>
          <div className="mb-7 text-center">
            <h2 className="text-[24px] font-semibold tracking-[-0.03em] text-[#17191C]">{t('forgotPasswordTitle')}</h2>
            <p className="mt-2 text-[14px] leading-6 text-[#969BA3]">{t('forgotPasswordSubtitle')}</p>
          </div>

          {error ? (
            <div role="alert" className="mb-5 flex items-center justify-between rounded-[10px] border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-[13px] text-[#B91C1C]">
              <span>{error}</span>
              <button type="button" onClick={() => setError('')} aria-label={t('dismissError')} className="ml-3 text-[#EF4444]">×</button>
            </div>
          ) : null}

          <form onSubmit={(event) => { event.preventDefault(); void handleSendCode(); }} className="space-y-5">
            <div className="space-y-2">
              <label htmlFor="auth-field-1" className="ml-0.5 text-[14px] font-semibold text-[#3F444B]">{t('email')}</label>
              <div className="relative">
                <Mail className="pointer-events-none absolute inset-y-0 left-0 my-auto ml-4 text-[#9CA3AF]" size={16} />
                <input id="auth-field-1"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder={t('emailPlaceholder')}
                  className="w-full rounded-[10px] border border-[#DDE1E5] bg-white py-4 pl-11 pr-4 text-[15px] text-[#111827] outline-none transition placeholder:text-[#A4A9B0] focus:border-[#4ABDE9] focus:shadow-[0_0_0_4px_rgba(74,189,233,0.10)]"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
            </div>
            <StudioAction
              type="submit"
              disabled={!email.trim() || isSendingCode}
              data-auth-primary
              className={`${formStyles.action} w-full gap-2`}
             variant="primary">
              {isSendingCode ? <SystemLoadingIcon className="h-4 w-4 animate-spin rounded-full border-2 border-[#203644]/20 border-t-[#203644]" /> : <><span>{t('sendCode')}</span><ArrowRight size={16} /></>}
            </StudioAction>
          </form>

          <button type="button" onClick={() => onBack(email)} className="mx-auto mt-7 flex items-center justify-center gap-2 text-[13px] font-medium text-[#6B7280] transition hover:text-[#111827]">
            <ArrowLeft size={15} />
            {t('backToSignIn')}
          </button>
        </>
      ) : (
        <>
          <div className="mb-7 text-center">
            <h2 className="text-[24px] font-semibold tracking-[-0.03em] text-[#17191C]">{t('resetPasswordTitle')}</h2>
            <p className="mt-2 text-[14px] leading-6 text-[#969BA3]">{t('codeSentToEmail', { email })}</p>
          </div>

          {error ? (
            <div role="alert" className="mb-5 flex items-center justify-between rounded-[10px] border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-[13px] text-[#B91C1C]">
              <span>{error}</span>
              <button type="button" onClick={() => setError('')} aria-label={t('dismissError')} className="ml-3 text-[#EF4444]">×</button>
            </div>
          ) : null}

          <form onSubmit={handleReset} className="space-y-5">
            <div className="space-y-2">
              <label htmlFor="auth-field-2" className="ml-0.5 text-[14px] font-semibold text-[#3F444B]">{t('verificationCode')}</label>
              <div className="relative">
                <Shield className="pointer-events-none absolute inset-y-0 left-0 my-auto ml-4 text-[#9CA3AF]" size={16} />
                <input id="auth-field-2"
                  type="text"
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder={t('codePlaceholder')}
                  className="w-full rounded-[10px] border border-[#DDE1E5] bg-white py-4 pl-11 pr-[108px] text-[15px] tracking-[0.08em] text-[#111827] outline-none transition placeholder:text-[#A4A9B0] focus:border-[#4ABDE9] focus:shadow-[0_0_0_4px_rgba(74,189,233,0.10)]"
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                />
                <button type="button" onClick={() => void handleSendCode()} disabled={isSendingCode || countdown > 0} className="absolute inset-y-1 right-1 flex w-24 items-center justify-center rounded-[8px] bg-[#F3F4F6] px-2 text-[12px] font-semibold text-[#4B5563] transition hover:bg-[#E5E7EB] disabled:cursor-not-allowed disabled:opacity-45">
                  {isSendingCode ? <SystemLoadingIcon className="h-4 w-4 animate-spin rounded-full border-2 border-[#CBD5E1] border-t-[#4ABDE9]" /> : countdown > 0 ? `${countdown}s` : codeSent ? t('resend') : t('sendCode')}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="auth-field-3" className="ml-0.5 text-[14px] font-semibold text-[#3F444B]">{t('newPassword')}</label>
              <div className="relative">
                <Lock className="pointer-events-none absolute inset-y-0 left-0 my-auto ml-4 text-[#9CA3AF]" size={16} />
                <input id="auth-field-3"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="new-password"
                  placeholder={t('atLeast6Chars')}
                  className="w-full rounded-[10px] border border-[#DDE1E5] bg-white py-4 pl-11 pr-12 text-[15px] text-[#111827] outline-none transition placeholder:text-[#A4A9B0] focus:border-[#4ABDE9] focus:shadow-[0_0_0_4px_rgba(74,189,233,0.10)]"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                />
                <button type="button" onClick={() => setShowPassword((current) => !current)} aria-label={showPassword ? t('hidePassword') : t('showPassword')} className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-[#6B7280] hover:text-[#111827]">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="auth-field-4" className="ml-0.5 text-[14px] font-semibold text-[#3F444B]">{t('confirmPassword')}</label>
              <div className="relative">
                <Lock className="pointer-events-none absolute inset-y-0 left-0 my-auto ml-4 text-[#9CA3AF]" size={16} />
                <input id="auth-field-4"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="new-password"
                  placeholder={t('reEnterPassword')}
                  className="w-full rounded-[10px] border border-[#DDE1E5] bg-white py-4 pl-11 pr-4 text-[15px] text-[#111827] outline-none transition placeholder:text-[#A4A9B0] focus:border-[#4ABDE9] focus:shadow-[0_0_0_4px_rgba(74,189,233,0.10)]"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                />
              </div>
            </div>

            <StudioAction type="submit" disabled={isLoading || code.length !== 6} data-auth-primary
              className={`${formStyles.action} w-full gap-2`} variant="primary">
              {isLoading ? <SystemLoadingIcon className="h-4 w-4 animate-spin rounded-full border-2 border-[#203644]/20 border-t-[#203644]" /> : <><span>{t('resetPassword')}</span><ArrowRight size={16} /></>}
            </StudioAction>
          </form>

          <button type="button" onClick={() => { setStep('email'); setError(''); }} className="mx-auto mt-7 flex items-center justify-center gap-2 text-[13px] font-medium text-[#6B7280] transition hover:text-[#111827]">
            <ArrowLeft size={15} />
            {t('backToChangeEmail')}
          </button>
        </>
      )}
    </div>
  );
}

export function LoginDialog({
  open,
  onClose,
  onAuthenticated,
  initialMethod = 'email',
  storedAttribution,
  wechatQrLoginEnabled,
}: LoginDialogProps) {
  const reduceMotion = useReducedMotion();
  const login = useAuthStore((state) => state.login);
  const loginWithCode = useAuthStore((state) => state.loginWithCode);
  const cancelLoginAttempt = useAuthStore((state) => state.cancelLoginAttempt);
  const loginWithGoogle = useAuthStore((state) => state.loginWithGoogle);
  const loginWithApple = useAuthStore((state) => state.loginWithApple);
  const completeAppleEmailBinding = useAuthStore((state) => state.completeAppleEmailBinding);
  const sendVerificationCode = useAuthStore((state) => state.sendVerificationCode);
  const startWechatQrLogin = useAuthStore((state) => state.startWechatQrLogin);
  const pollWechatQrLogin = useAuthStore((state) => state.pollWechatQrLogin);
  const consumeWechatQrLogin = useAuthStore((state) => state.consumeWechatQrLogin);
  const cancelWechatQrLogin = useAuthStore((state) => state.cancelWechatQrLogin);
  const completeWechatEmailBinding = useAuthStore((state) => state.completeWechatEmailBinding);
  const t = useTranslations('auth');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [referralCode, setReferralCode] = useState(storedAttribution?.referralCode ?? '');
  useEffect(() => {
    if (open) rememberReferralClaim(referralCode);
  }, [open, referralCode]);
  const [referralInputOpen, setReferralInputOpen] = useState(false);
  const [loginMethod, setLoginMethod] = useState<LoginMethod>(initialMethod);
  const [loginEntryMode, setLoginEntryMode] = useState<LoginEntryMode>('choices');
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [emailLoginMode, setEmailLoginMode] = useState<EmailLoginMode>('code');
  const [googleScriptReady, setGoogleScriptReady] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [appleScriptReady, setAppleScriptReady] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [wechatStatus, setWechatStatus] = useState<WechatPanelStatus>('idle');
  const [wechatSessionId, setWechatSessionId] = useState('');
  const [wechatSessionToken, setWechatSessionToken] = useState('');
  const [wechatQrUrl, setWechatQrUrl] = useState('');
  const [wechatExpiresAt, setWechatExpiresAt] = useState(0);
  const [wechatSecondsRemaining, setWechatSecondsRemaining] = useState(0);
  const [wechatQrFrameLoaded, setWechatQrFrameLoaded] = useState(false);
  const [wechatQrFrameFailed, setWechatQrFrameFailed] = useState(false);
  const [wechatQrFrameRevealed, setWechatQrFrameRevealed] = useState(false);
  const [wechatQrLoadSlow, setWechatQrLoadSlow] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const consentCheckboxRef = useRef<HTMLInputElement | null>(null);
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const googleInitializedRef = useRef(false);
  const googleCredentialHandlerRef = useRef<(credential?: string) => void>(() => undefined);
  const googleLoginActiveRef = useRef(false);
  const googleSubmissionRef = useRef<number | null>(null);
  const [wechatCallbackReceived, setWechatCallbackReceived] = useState(false);
  const [wechatBindTicket, setWechatBindTicket] = useState('');
  const [appleBindTicket, setAppleBindTicket] = useState('');
  const [wechatIdentityNickname, setWechatIdentityNickname] = useState('');
  const [wechatStatusMessage, setWechatStatusMessage] = useState('');
  const [wechatCheckingStatus, setWechatCheckingStatus] = useState(false);
  const [wechatReplacing, setWechatReplacing] = useState(false);
  const wechatPollInFlightRef = useRef(false);
  const wechatRedirectedRef = useRef(false);
  const wechatCallbackRetryTimerRef = useRef<number | null>(null);
  const loginSubmissionIdRef = useRef(0);
  const wechatSessionGenerationRef = useRef(0);
  const wechatRequestAbortRef = useRef<AbortController | null>(null);
  void wechatQrLoginEnabled;
  const wechatLoginVisible = false;
  const googleLoginAvailable = Boolean(GOOGLE_CLIENT_ID);
  const appleLoginAvailable = Boolean(APPLE_CLIENT_ID);

  useEffect(() => () => {
    loginSubmissionIdRef.current += 1;
    googleLoginActiveRef.current = false;
    wechatRequestAbortRef.current?.abort();
    cancelLoginAttempt();
  }, [cancelLoginAttempt]);

  useEffect(() => {
    if (!open || !googleLoginAvailable) return;
    let cancelled = false;
    void loadGoogleIdentityScript().then((ready) => {
      if (!cancelled) setGoogleScriptReady(ready);
    });
    return () => { cancelled = true; };
  }, [open, googleLoginAvailable]);

  const requireConsent = useCallback(() => {
    if (consentChecked) return true;
    setError('');
    setConsentError(true);
    consentCheckboxRef.current?.focus();
    return false;
  }, [consentChecked]);

  const isWechatAwaitingBind = loginMethod === 'wechat' && wechatStatus === 'email-required';
  const isAppleAwaitingBind = loginMethod === 'apple' && Boolean(appleBindTicket);
  const isProviderAwaitingBind = isWechatAwaitingBind || isAppleAwaitingBind;
  const showEmailForm = loginEntryMode === 'email' || isProviderAwaitingBind;
  googleLoginActiveRef.current = open && consentChecked && loginMethod === 'email' && loginEntryMode === 'choices' && !showEmailForm;
  useEffect(() => {
    googleLoginActiveRef.current = open && consentChecked && loginMethod === 'email' && loginEntryMode === 'choices' && !showEmailForm;
    return () => { googleLoginActiveRef.current = false; };
  }, [open, consentChecked, loginMethod, loginEntryMode, showEmailForm]);
  const showEmailCodeForm = showEmailForm && (isProviderAwaitingBind || emailLoginMode === 'code');
  const showPasswordForm = showEmailForm && loginMethod === 'email' && emailLoginMode === 'password';
  const showWechatQrLoading = loginMethod === 'wechat' && !showEmailForm && (
    wechatStatus === 'booting'
    || !wechatQrUrl
    || (!wechatQrFrameRevealed && !wechatQrFrameFailed)
    || wechatQrFrameFailed
    || wechatCallbackReceived
  );
  const wechatQrHelperText = wechatCallbackReceived
    ? t('wechatConfirming')
    : wechatQrFrameFailed
      ? t('wechatQrLoadFailed')
      : wechatQrLoadSlow
        ? t('wechatQrSlow')
        : wechatQrFrameLoaded
          ? t('wechatAwaitingScan')
          : t('wechatLoadingQr');
  const showWechatQrInlineRefresh = loginMethod === 'wechat' && !showEmailForm && wechatStatus === 'pending';

  const handleGoogleCredential = useCallback(async (credential?: string) => {
    // GIS can deliver a callback after the dialog was dismissed or another method selected.
    if (!googleLoginActiveRef.current || googleSubmissionRef.current !== null) return;
    if (!credential) {
      setIsGoogleLoading(false);
      setError(t('googleLoginFailed'));
      return;
    }

    const submissionId = ++loginSubmissionIdRef.current;
    googleSubmissionRef.current = submissionId;
    setIsGoogleLoading(true);
    setError('');
    try {
      const authenticated = await loginWithGoogle(credential, buildAuthReferralPayload(storedAttribution, referralCode));
      if (!authenticated || submissionId !== loginSubmissionIdRef.current || !googleLoginActiveRef.current) return;
      onAuthenticated();
    } catch (err) {
      if (submissionId !== loginSubmissionIdRef.current || !googleLoginActiveRef.current) return;
      setError(getUserFacingErrorMessage(err, 'auth', t('googleLoginFailed')));
    } finally {
      if (googleSubmissionRef.current === submissionId) {
        googleSubmissionRef.current = null;
        setIsGoogleLoading(false);
      }
    }
  }, [loginWithGoogle, onAuthenticated, referralCode, storedAttribution, t]);

  useEffect(() => {
    googleCredentialHandlerRef.current = (credential) => {
      void handleGoogleCredential(credential);
    };
  }, [handleGoogleCredential]);

  useEffect(() => {
    const googleIdentity = window.google?.accounts?.id;
    const buttonContainer = googleButtonRef.current;
    if (
      !open
      || loginEntryMode !== 'choices'
      || !consentChecked
      || !googleLoginAvailable
      || !googleScriptReady
      || !googleIdentity
      || !buttonContainer
    ) {
      return;
    }

    if (!googleInitializedRef.current) {
      googleIdentity.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response) => {
          googleCredentialHandlerRef.current(response.credential);
        },
        ux_mode: 'popup',
        // Keep the button text stable instead of turning it into a compact
        // account card for returning Google sessions.
        use_fedcm_for_button: false,
        button_auto_select: false,
      });
      googleInitializedRef.current = true;
    }

    // GIS owns its iframe and supports widths up to 400px. Measure layout
    // dimensions, not the dialog's animated transform, and rerender on resize.
    let renderedWidth = 0;
    const renderGoogleButton = () => {
      const width = Math.min(400, buttonContainer.clientWidth);
      if (width <= 0 || width === renderedWidth) return;
      renderedWidth = width;
      buttonContainer.replaceChildren();
      googleIdentity.renderButton(buttonContainer, {
        type: 'standard',
        theme: 'outline',
        // Use the largest native GIS button; its own outline is the only
        // visible border, so the row does not look like a button in a button.
        size: 'large',
        text: 'continue_with',
        shape: 'pill',
        logo_alignment: 'center',
        width,
      });
    };
    renderGoogleButton();
    // GIS replaces its iframe, changing the observed height. Keep that DOM write
    // outside native resize delivery so WebKit does not enter a resize loop.
    const resizeFrame = createAnimationFrameTask(renderGoogleButton);
    const observer = createDiagnosedResizeObserver('LoginDialog.GoogleButton', resizeFrame.schedule);
    observer.observe(buttonContainer);
    return () => {
      observer.disconnect();
      resizeFrame.dispose();
      buttonContainer.replaceChildren();
    };
  }, [consentChecked, googleLoginAvailable, googleScriptReady, loginEntryMode, open]);

  const handleUnavailableGoogleLogin = useCallback(async () => {
    if (!requireConsent()) return;
    if (!googleLoginAvailable) {
      setError(t('googleLoginNotConfigured'));
      return;
    }
    if (isGoogleLoading) return;
    setError('');
    setIsGoogleLoading(true);
    const ready = await loadGoogleIdentityScript();
    setGoogleScriptReady(ready);
    setIsGoogleLoading(false);
    if (!ready) setError(t('googleLoginUnavailable'));
  }, [googleLoginAvailable, isGoogleLoading, requireConsent, t]);

  const startAppleLogin = useCallback(async () => {
    if (!requireConsent()) return;
    if (!appleLoginAvailable) {
      setError(t('appleLoginNotConfigured'));
      return;
    }
    if (!appleScriptReady || !window.AppleID?.auth) {
      setError(t('appleLoginLoading'));
      return;
    }

    setIsAppleLoading(true);
    setError('');
    try {
      const redirectURI = APPLE_REDIRECT_URI || `${window.location.origin}/login`;
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI,
        state: `login-${Date.now()}`,
        nonce: crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`,
        usePopup: true,
      });
      const appleResult = await window.AppleID.auth.signIn();
      const identityToken = appleResult.authorization?.id_token;
      if (!identityToken) {
        throw new Error(t('appleLoginFailed'));
      }
      const loginResult = await loginWithApple(identityToken, {
        authorizationCode: appleResult.authorization?.code,
        email: appleResult.user?.email,
        fullName: appleResult.user?.name
          ? {
              givenName: appleResult.user.name.firstName,
              familyName: appleResult.user.name.lastName,
            }
          : undefined,
        referral: buildAuthReferralPayload(storedAttribution, referralCode),
      });
      if (loginResult.status === 'email-required') {
        setAppleBindTicket(loginResult.bindTicket);
        setLoginMethod('apple');
        setLoginEntryMode('email');
        setEmailLoginMode('code');
        setVerificationCode('');
        setCodeSent(false);
        setCountdown(0);
        return;
      }
      onAuthenticated();
    } catch (err) {
      setError(getUserFacingErrorMessage(err, 'auth', t('appleLoginFailed')));
    } finally {
      setIsAppleLoading(false);
    }
  }, [appleLoginAvailable, appleScriptReady, loginWithApple, onAuthenticated, referralCode, requireConsent, storedAttribution, t]);

  const resetWechatSessionUi = useCallback(() => {
    wechatSessionGenerationRef.current += 1;
    wechatRequestAbortRef.current?.abort();
    wechatRequestAbortRef.current = null;
    setWechatStatus('idle');
    setWechatSessionId('');
    setWechatSessionToken('');
    setWechatQrUrl('');
    setWechatExpiresAt(0);
    setWechatSecondsRemaining(0);
    setWechatQrFrameLoaded(false);
    setWechatQrFrameFailed(false);
    setWechatQrFrameRevealed(false);
    setWechatQrLoadSlow(false);
    setWechatCallbackReceived(false);
    setWechatBindTicket('');
    setWechatIdentityNickname('');
    setWechatStatusMessage('');
    setWechatCheckingStatus(false);
    setWechatReplacing(false);
    wechatPollInFlightRef.current = false;
    wechatRedirectedRef.current = false;
    if (wechatCallbackRetryTimerRef.current !== null) {
      window.clearTimeout(wechatCallbackRetryTimerRef.current);
      wechatCallbackRetryTimerRef.current = null;
    }
  }, []);

  const applyWechatSessionStatus = useCallback((session: WechatQrSessionStatusData) => {
    setWechatStatus(session.status);
    if (session.status === 'email-required') {
      setWechatBindTicket(session.bind_ticket ?? '');
      setWechatIdentityNickname(session.wechat_profile?.nickname ?? '');
      setWechatStatusMessage('');
    } else if (session.status === 'completed') {
      setWechatStatusMessage(t('wechatLoginSuccess'));
      setWechatCallbackReceived(true);
      if (!wechatRedirectedRef.current) {
        wechatRedirectedRef.current = true;
        window.setTimeout(() => {
          onAuthenticated();
        }, 120);
      }
    } else if (session.status === 'failed') {
      setWechatStatusMessage(session.error || t('wechatStatusFailed'));
    } else if (session.status === 'expired') {
      setWechatStatusMessage(t('wechatQrExpired'));
    } else {
      setWechatStatusMessage('');
    }
  }, [onAuthenticated, t]);

  const resolveWechatSessionStatus = useCallback(async (
    session: WechatQrSessionStatusData,
    generation: number,
  ) => {
    if (generation !== wechatSessionGenerationRef.current) {
      return;
    }
    if (session.status !== 'authorized') {
      applyWechatSessionStatus(session);
      return;
    }

    setWechatStatus('authorized');
    setWechatCallbackReceived(true);
    try {
      const completed = await consumeWechatQrLogin(wechatSessionId, wechatSessionToken);
      if (generation === wechatSessionGenerationRef.current) {
        applyWechatSessionStatus(completed);
      }
    } catch (err) {
      if (generation !== wechatSessionGenerationRef.current || (err instanceof DOMException && err.name === 'AbortError')) {
        return;
      }
      setWechatStatus('failed');
      setWechatStatusMessage(getUserFacingErrorMessage(err, 'auth', t('wechatStatusFailed')));
    }
  }, [applyWechatSessionStatus, consumeWechatQrLogin, t, wechatSessionId, wechatSessionToken]);

  useEffect(() => {
    if (countdown <= 0) {
      return;
    }

    const timer = window.setTimeout(() => setCountdown((current) => current - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    if (!open || loginMethod !== 'wechat' || wechatStatus !== 'pending' || !wechatExpiresAt) {
      return;
    }

    const updateRemaining = () => {
      const remaining = Math.max(0, Math.ceil((wechatExpiresAt - Date.now()) / 1000));
      setWechatSecondsRemaining(remaining);
      if (remaining === 0) {
        wechatRequestAbortRef.current?.abort();
        setWechatStatus('expired');
        setWechatStatusMessage(t('wechatQrExpired'));
      }
    };
    updateRemaining();
    const timer = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(timer);
  }, [loginMethod, open, t, wechatExpiresAt, wechatStatus]);

  useEffect(() => {
    setReferralCode(storedAttribution?.referralCode ?? '');
  }, [storedAttribution?.referralCode]);

  useEffect(() => {
    if (!open) {
      googleLoginActiveRef.current = false;
      googleSubmissionRef.current = null;
      setIsGoogleLoading(false);
      setEmail('');
      setPassword('');
      setPasswordVisible(false);
      setError('');
      setIsLoading(false);
      setVerificationCode('');
      setCodeSent(false);
      setReferralCode(storedAttribution?.referralCode ?? '');
      setReferralInputOpen(false);
      setCountdown(0);
      setLoginMethod(initialMethod);
      setLoginEntryMode('choices');
      setForgotPasswordOpen(false);
      setEmailLoginMode('code');
      resetWechatSessionUi();
    }
  }, [initialMethod, open, resetWechatSessionUi, storedAttribution?.referralCode]);

  useEffect(() => {
    if (wechatLoginVisible || loginMethod !== 'wechat') {
      return;
    }

    setLoginMethod('email');
    resetWechatSessionUi();
  }, [loginMethod, resetWechatSessionUi, wechatLoginVisible]);

  const initializeWechatSession = useCallback(async () => {
    setError('');
    resetWechatSessionUi();
    setWechatStatus('booting');
    const generation = wechatSessionGenerationRef.current;
    const controller = new AbortController();
    wechatRequestAbortRef.current = controller;

    try {
      const session = await startWechatQrLogin(controller.signal);
      if (controller.signal.aborted || generation !== wechatSessionGenerationRef.current) {
        return;
      }
      setWechatSessionId(session.session_id);
      setWechatSessionToken(session.session_token);
      setWechatQrUrl(buildWechatQrFrameUrl(session.qr_url));
      setWechatExpiresAt(Date.now() + session.expires_in * 1000);
      setWechatSecondsRemaining(session.expires_in);
      setWechatStatus('pending');
    } catch (err) {
      if (controller.signal.aborted || generation !== wechatSessionGenerationRef.current) {
        return;
      }
      setWechatStatus('failed');
      setWechatStatusMessage(getUserFacingErrorMessage(err, 'auth', t('wechatStartFailed')));
    }
  }, [resetWechatSessionUi, startWechatQrLogin, t]);

  const refreshWechatSessionStatus = useCallback(async (options?: { markCallbackReceived?: boolean }) => {
    if (!wechatSessionId || !wechatSessionToken || wechatPollInFlightRef.current) {
      return;
    }

    if (options?.markCallbackReceived) {
      setWechatCallbackReceived(true);
    }

    setWechatCheckingStatus(true);
    wechatPollInFlightRef.current = true;
    const generation = wechatSessionGenerationRef.current;
    try {
      const session = await pollWechatQrLogin(
        wechatSessionId,
        wechatSessionToken,
        wechatRequestAbortRef.current?.signal,
      );
      await resolveWechatSessionStatus(session, generation);
    } catch (err) {
      if (generation === wechatSessionGenerationRef.current && !(err instanceof DOMException && err.name === 'AbortError')) {
        setWechatStatus('failed');
        setWechatStatusMessage(getUserFacingErrorMessage(err, 'auth', t('wechatStatusFailed')));
      }
    } finally {
      if (generation === wechatSessionGenerationRef.current) {
        wechatPollInFlightRef.current = false;
        setWechatCheckingStatus(false);
      }
    }
  }, [pollWechatQrLogin, resolveWechatSessionStatus, t, wechatSessionId, wechatSessionToken]);

  useEffect(() => {
    if (!open || loginMethod !== 'wechat' || wechatStatus !== 'idle') {
      return;
    }

    void initializeWechatSession();
  }, [initializeWechatSession, loginMethod, open, wechatStatus]);

  useEffect(() => {
    if (!open || loginMethod !== 'wechat' || !wechatQrUrl || wechatQrFrameLoaded || wechatQrFrameFailed) {
      return;
    }

    const timer = window.setTimeout(() => setWechatQrLoadSlow(true), WECHAT_QR_SLOW_LOAD_MS);
    return () => window.clearTimeout(timer);
  }, [loginMethod, open, wechatQrFrameFailed, wechatQrFrameLoaded, wechatQrUrl]);

  useEffect(() => {
    if (!open || loginMethod !== 'wechat' || !wechatQrUrl || wechatQrFrameRevealed || wechatQrFrameFailed) {
      return;
    }

    const timer = window.setTimeout(() => setWechatQrFrameRevealed(true), WECHAT_QR_INITIAL_REVEAL_MS);
    return () => window.clearTimeout(timer);
  }, [loginMethod, open, wechatQrFrameFailed, wechatQrFrameRevealed, wechatQrUrl]);

  useEffect(() => {
    if (!open || loginMethod !== 'wechat' || !wechatSessionId || !wechatSessionToken || wechatStatus !== 'pending') {
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    const generation = wechatSessionGenerationRef.current;

    const pollStatus = async () => {
      if (wechatPollInFlightRef.current) {
        return;
      }

      wechatPollInFlightRef.current = true;
      setWechatCheckingStatus(true);
      try {
        const session = await pollWechatQrLogin(
          wechatSessionId,
          wechatSessionToken,
          wechatRequestAbortRef.current?.signal,
        );
        if (cancelled || generation !== wechatSessionGenerationRef.current) {
          return;
        }

        await resolveWechatSessionStatus(session, generation);
      } catch (err) {
        if (!cancelled && generation === wechatSessionGenerationRef.current && !(err instanceof DOMException && err.name === 'AbortError')) {
          setWechatStatus('failed');
          setWechatStatusMessage(getUserFacingErrorMessage(err, 'auth', t('wechatStatusFailed')));
        }
      } finally {
        if (generation === wechatSessionGenerationRef.current) {
          wechatPollInFlightRef.current = false;
        }
        if (!cancelled && generation === wechatSessionGenerationRef.current) {
          setWechatCheckingStatus(false);
        }
      }

      if (!cancelled) {
        timer = window.setTimeout(() => {
          void pollStatus();
        }, WECHAT_QR_POLL_INTERVAL_MS);
      }
    };

    void pollStatus();
    return () => {
      cancelled = true;
      wechatPollInFlightRef.current = false;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [loginMethod, open, pollWechatQrLogin, resolveWechatSessionStatus, t, wechatSessionId, wechatSessionToken, wechatStatus]);

  useEffect(() => {
    if (!open || loginMethod !== 'wechat' || !wechatSessionId) {
      return;
    }

    const handleWechatCallbackMessage = (event: MessageEvent<unknown>) => {
      if (!isTrustedWechatCallbackOrigin(event.origin) || !isWechatQrCallbackMessage(event.data)) {
        return;
      }
      if (event.data.state !== wechatSessionId) {
        return;
      }

      if (event.data.status === 'failed' || event.data.status === 'expired') {
        wechatRequestAbortRef.current?.abort();
        setWechatCallbackReceived(false);
        setWechatStatus(event.data.status);
        setWechatStatusMessage(event.data.status === 'expired' ? t('wechatQrExpired') : t('wechatStatusFailed'));
        return;
      }

      setWechatCallbackReceived(true);
      if (wechatPollInFlightRef.current) {
        if (wechatCallbackRetryTimerRef.current !== null) {
          window.clearTimeout(wechatCallbackRetryTimerRef.current);
        }
        wechatCallbackRetryTimerRef.current = window.setTimeout(() => {
          wechatCallbackRetryTimerRef.current = null;
          void refreshWechatSessionStatus({ markCallbackReceived: true });
        }, 700);
        return;
      }

      void refreshWechatSessionStatus({ markCallbackReceived: true });
    };

    window.addEventListener('message', handleWechatCallbackMessage);
    return () => {
      window.removeEventListener('message', handleWechatCallbackMessage);
      if (wechatCallbackRetryTimerRef.current !== null) {
        window.clearTimeout(wechatCallbackRetryTimerRef.current);
        wechatCallbackRetryTimerRef.current = null;
      }
    };
  }, [loginMethod, open, refreshWechatSessionStatus, t, wechatSessionId]);

  const handleSendCode = async () => {
    if (!requireConsent()) return;
    if (!email || isSendingCode || countdown > 0) {
      return;
    }

    if (loginMethod === 'wechat' && wechatStatus !== 'email-required') {
      return;
    }

    if (loginMethod === 'apple' && !appleBindTicket) {
      return;
    }

    setIsSendingCode(true);
    setError('');

    try {
      await sendVerificationCode(email, 'login');
      setCodeSent(true);
      setCountdown(60);
    } catch (err) {
      setError(getUserFacingErrorMessage(err, 'auth', t('loginFailed')));
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleLogin = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    if (!requireConsent()) return;
    const submissionId = loginSubmissionIdRef.current + 1;
    loginSubmissionIdRef.current = submissionId;
    setError('');
    setIsLoading(true);

    try {
      if (loginMethod === 'email' && emailLoginMode === 'password') {
        if (!password) {
          setError(t('enterPassword'));
          setIsLoading(false);
          return;
        }

        const authenticated = await login(email.trim(), password);
        if (!authenticated || submissionId !== loginSubmissionIdRef.current) return;
        onAuthenticated();
        return;
      }

      if (!verificationCode || verificationCode.length !== 6) {
        setError(t('enterVerificationCode'));
        setIsLoading(false);
        return;
      }

      const referralPayload = buildAuthReferralPayload(storedAttribution, referralCode);

      if (loginMethod === 'wechat') {
        if (wechatStatus !== 'email-required' || !wechatBindTicket) {
          setError(t('wechatBindRequiredError'));
          setIsLoading(false);
          return;
        }

        await completeWechatEmailBinding(
          wechatBindTicket,
          email,
          verificationCode,
          wechatSessionToken,
          referralPayload,
        );
      } else if (loginMethod === 'apple') {
        if (!appleBindTicket) {
          setError(t('appleBindRequiredError'));
          setIsLoading(false);
          return;
        }

        await completeAppleEmailBinding(
          appleBindTicket,
          email,
          verificationCode,
          referralPayload,
        );
      } else {
        const authenticated = await loginWithCode(email.trim(), verificationCode, referralPayload);
        if (!authenticated || submissionId !== loginSubmissionIdRef.current) return;
      }

      if (submissionId !== loginSubmissionIdRef.current) return;
      onAuthenticated();
    } catch (err) {
      if (submissionId !== loginSubmissionIdRef.current) return;
      const message = getUserFacingErrorMessage(err, 'auth', t('loginFailed'));
      if (loginMethod === 'wechat' && message === t('wechatBindExpired')) {
        setWechatStatus('expired');
        setWechatStatusMessage(message);
      }
      setError(message);
      setIsLoading(false);
    }
  }, [appleBindTicket, completeAppleEmailBinding, completeWechatEmailBinding, email, emailLoginMode, login, loginMethod, loginWithCode, onAuthenticated, password, referralCode, requireConsent, storedAttribution, t, verificationCode, wechatBindTicket, wechatSessionToken, wechatStatus]);

  const handleClose = useCallback(async () => {
    const sessionToCancel = wechatSessionId;
    const tokenToCancel = wechatSessionToken;
    loginSubmissionIdRef.current += 1;
    googleLoginActiveRef.current = false;
    googleSubmissionRef.current = null;
    setIsGoogleLoading(false);
    setConsentChecked(false);
    setConsentError(false);
    cancelLoginAttempt();
    resetWechatSessionUi();
    onClose();
    if (sessionToCancel && tokenToCancel) {
      try {
        await cancelWechatQrLogin(sessionToCancel, tokenToCancel);
      } catch {
        // 本地凭证和轮询已经销毁；服务端残留会按短 TTL 自动过期。
      }
    }
  }, [cancelLoginAttempt, cancelWechatQrLogin, onClose, resetWechatSessionUi, wechatSessionId, wechatSessionToken]);

  useEffect(() => {
    if (!open) return;

    const previousFocus = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const focusTarget = dialogRef.current?.querySelector<HTMLElement>('[data-login-autofocus]');
    const focusTimer = window.setTimeout(() => focusTarget?.focus({ preventScroll: true }), 0);
    const releaseScrollLock = acquireBodyScrollLock();
    const backdrop = dialogRef.current?.parentElement;
    const background = Array.from(document.body.children)
      .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== backdrop && !element.contains(backdrop ?? null))
      .map((element) => ({ element, inert: element.inert }));
    background.forEach(({ element }) => { element.inert = true; });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        void handleClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), a[href], iframe, [tabindex]:not([tabindex="-1"])',
      )).filter((element) => element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden');
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialogRef.current.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', handleKeyDown);
      releaseScrollLock();
      background.forEach(({ element, inert }) => { element.inert = inert; });
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [handleClose, open]);

  const handleMethodChange = useCallback(async (method: LoginMethod) => {
    if (method === 'wechat' && !wechatLoginVisible) {
      return;
    }

    const sessionToCancel = wechatSessionId;
    const tokenToCancel = wechatSessionToken;
    loginSubmissionIdRef.current += 1;
    cancelLoginAttempt();
    setError('');
    setLoginMethod(method);
    if (method !== 'wechat') {
      resetWechatSessionUi();
      if (sessionToCancel && tokenToCancel) {
        try {
          await cancelWechatQrLogin(sessionToCancel, tokenToCancel);
        } catch {
          // 浏览器侧凭证已销毁，旧会话无法再领取登录结果。
        }
      }
    }
  }, [cancelLoginAttempt, cancelWechatQrLogin, resetWechatSessionUi, wechatLoginVisible, wechatSessionId, wechatSessionToken]);

  const handleReplaceWechat = useCallback(async () => {
    const sessionToCancel = wechatSessionId;
    const tokenToCancel = wechatSessionToken;
    loginSubmissionIdRef.current += 1;
    cancelLoginAttempt();
    resetWechatSessionUi();
    setWechatReplacing(true);
    if (sessionToCancel && tokenToCancel) {
      try {
        await cancelWechatQrLogin(sessionToCancel, tokenToCancel);
      } catch {
        // 旧会话的浏览器凭证已经销毁，可以安全创建新会话。
      }
    }
    await initializeWechatSession();
  }, [cancelLoginAttempt, cancelWechatQrLogin, initializeWechatSession, resetWechatSessionUi, wechatSessionId, wechatSessionToken]);

  const handleEmailEntryClick = useCallback(() => {
    if (!requireConsent()) return;
    setError('');
    setAppleBindTicket('');
    setLoginMethod('email');
    setLoginEntryMode('email');
    setEmailLoginMode('code');
  }, [requireConsent]);

  const handleForgotPasswordBack = useCallback((forgotEmail?: string) => {
    setForgotPasswordOpen(false);
    setError('');
    if (forgotEmail?.trim()) {
      setEmail(forgotEmail.trim());
    }
    setLoginMethod('email');
    setLoginEntryMode('email');
    setEmailLoginMode('password');
  }, []);

  const inputClass = 'w-full rounded-[10px] border border-[#DDE1E5] bg-white py-4 text-[15px] text-[#111827] outline-none transition placeholder:text-[#A4A9B0] focus:border-[#4ABDE9] focus:bg-white focus:shadow-[0_0_0_4px_rgba(74,189,233,0.10)]';
  const inviteInputClass = 'w-full rounded-[8px] border border-[#E5E7EB] bg-white py-3 text-[13px] text-[#111827] outline-none transition placeholder:text-[#9CA3AF] focus:border-[#4ABDE9] focus:bg-white focus:shadow-[0_0_0_4px_rgba(74,189,233,0.08)]';

  return (
    <>
      <Script
        src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"
        strategy="afterInteractive"
        onLoad={() => setAppleScriptReady(true)}
        onError={() => setError(t('appleLoginFailed'))}
      />
      <AnimatePresence>
        {open ? (
        <motion.div
          className={`${formStyles.backdrop} fixed inset-0 z-[1200] flex items-center justify-center overflow-hidden bg-[#203644]/40 p-3 backdrop-blur-[3px] sm:p-5`}
          initial={false}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          {
            <motion.div
              className="absolute inset-0"
              onClick={handleClose}
              initial={false}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
          }

          <motion.div
            ref={dialogRef}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key !== 'Escape') return;
              event.preventDefault();
              event.stopPropagation();
              void handleClose();
            }}
            className={`${formStyles.shell} login-dialog-shell relative flex h-[560px] max-h-[calc(100svh-24px)] w-full max-w-[880px] overflow-hidden bg-white text-[#203644]`}
            initial={reduceMotion ? false : { y: 18, scale: 0.985 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.985 }}
            transition={{ duration: reduceMotion ? 0 : 0.28, ease: [0.16, 1, 0.3, 1] }}
            aria-modal="true"
            role="dialog"
            aria-labelledby={forgotPasswordOpen ? 'forgot-password-dialog-title' : 'login-dialog-title'}
          >
            <button
              type="button"
              onClick={handleClose}
              aria-label={t('closeLoginDialog')}
              data-login-autofocus
              className="absolute right-2 top-2 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-[#D9EDF6] bg-white text-[#577185] transition hover:border-[#4ABDE9] hover:text-[#173349] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#4ABDE9]/30"
            >
              <X size={18} strokeWidth={1.8} />
            </button>
            <LoginImagePanel />
            <div className={`${formStyles.content} login-dialog-content relative min-h-0 min-w-0 flex-1 overflow-y-auto px-5 pb-7 pt-5 sm:px-8`}>

                <div className={`${formStyles.stack} mx-auto flex min-h-full w-full max-w-[400px] flex-col`}>
                  <BrandIcon
                    alt="dobidobi"
                    width={40}
                    height={40}
                    priority
                    className="login-dialog-logo mx-auto block h-10 w-10"
                  />
                  {forgotPasswordOpen ? (
                    <ForgotPasswordPanel initialEmail={email} onBack={handleForgotPasswordBack} />
                  ) : (
                    <>
                  <header id="login-dialog-title" className="login-dialog-header mt-2 text-center">
                    <h1 className="login-dialog-title text-[30px] font-semibold tracking-[-0.04em] text-[#173349]">
                      {t('loginWelcomeTitle')}
                    </h1>
                  </header>
                  <LoginRewardBanner />
                  {error ? (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      role="alert"
                      className="mt-6 flex items-center justify-between rounded-[10px] border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-[13px] text-[#B91C1C]"
                    >
                      <span>{error}</span>
                      <button
                        type="button"
                        onClick={() => setError('')}
                        aria-label={t('dismissError')}
                        className="ml-3 shrink-0 text-[#EF4444] transition-colors hover:text-[#B91C1C]"
                      >
                        <X size={14} />
                      </button>
                    </motion.div>
                  ) : null}

                  {SHOW_REFERRAL_CONTROLS ? (
                    <AuthReferralBanner
                      className="mt-4"
                      attribution={storedAttribution}
                    />
                  ) : null}

                  {loginEntryMode === 'choices' ? (
                    <div className={`${formStyles.signInChoices} mt-3.5 w-full`}>
                      <div className="mx-auto w-full max-w-[400px] space-y-2">
                        {consentChecked && googleLoginAvailable && googleScriptReady ? (
                          <div className="relative flex min-h-11 w-full items-center justify-center">
                            <div ref={googleButtonRef} className="flex w-full items-center justify-center" />
                            {isGoogleLoading ? (
                              <div className="absolute inset-0 flex items-center justify-center rounded-[10px] bg-white/90" aria-label={t('googleLoginLoading')}>
                                <SystemLoadingIcon className="h-5 w-5 animate-spin rounded-full border-2 border-[#4ABDE9]/25 border-t-[#4ABDE9]" />
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <GoogleLoginButton
                            onClick={() => { void handleUnavailableGoogleLogin(); }}
                            isLoading={isGoogleLoading}
                            label={t('continueWithGoogle')}
                            surface="light"
                            size="large"
                            className={`${formStyles.choices} !min-h-10 !border-[#D5EAF4] !py-0 !text-[14px]`}
                          />
                        )}
                        <AppleLoginButton
                          onClick={() => void startAppleLogin()}
                          isLoading={isAppleLoading}
                          label={t('continueWithApple')}
                          surface="light"
                          size="large"
                          className={`${formStyles.choices} !min-h-10 !py-0 !text-[14px]`}
                        />
                        <div className="flex items-center justify-center py-0.5 text-[12px] text-[#8195A2]">{t('loginDividerOr')}</div>
                        <StudioAction
                          type="button"
                          onClick={handleEmailEntryClick}
                          data-login-primary
                          className={`${formStyles.action} w-full gap-2`}
                         variant="secondary">
                          <Mail size={18} className="shrink-0 text-[#3B9FC6]" />
                          <span>{t('continueWithEmail')}</span>
                        </StudioAction>
                        <label className="flex cursor-pointer items-start gap-3 pt-1 text-[12px] leading-[17px] text-[#50697A]">
                          <input
                            ref={consentCheckboxRef}
                            type="checkbox"
                            checked={consentChecked}
                            aria-invalid={consentError}
                            aria-describedby={consentError ? 'login-consent-help' : undefined}
                            onChange={(event) => {
                              setConsentChecked(event.target.checked);
                              if (event.target.checked) {
                                setConsentError(false);
                                setError('');
                              }
                            }}
                            className="mt-0.5 h-[18px] w-[18px] shrink-0 cursor-pointer accent-[#4ABDE9]"
                          />
                          <span>
                            {t.rich('loginConsentLabel', {
                              terms: (chunks) => <Link href="/terms" target="_blank" className="font-semibold text-[#167BA4] underline underline-offset-2" onClick={(event) => event.stopPropagation()}>{chunks}</Link>,
                              privacy: (chunks) => <Link href="/privacy" target="_blank" className="font-semibold text-[#167BA4] underline underline-offset-2" onClick={(event) => event.stopPropagation()}>{chunks}</Link>,
                            })}
                          </span>
                        </label>
                        {consentError ? (
                          <p id="login-consent-help" aria-live="polite" className="pl-[30px] text-[12px] leading-5 text-[#577185]">
                            {t('loginConsentRequired')}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  {wechatLoginVisible ? (
                    <div className="login-dialog-method-tabs mx-auto mt-8 grid w-full max-w-[560px] grid-cols-2 overflow-hidden rounded-[10px] border border-[#DDE1E5] bg-white">
                      <button
                        type="button"
                        onClick={() => handleMethodChange('email')}
                        aria-pressed={loginMethod === 'email'}
                        className={`login-dialog-method-button flex h-[46px] items-center justify-center gap-2 text-[13px] font-medium transition focus:outline-none focus-visible:underline focus-visible:decoration-2 focus-visible:underline-offset-4 ${
                          loginMethod === 'email'
                            ? 'bg-[#EDF9FD] text-[#1599CC]'
                            : 'text-[#4F545B] hover:bg-[#F7F8F9] hover:text-[#17191C]'
                        }`}
                      >
                        <Mail size={15} strokeWidth={1.8} className="shrink-0" />
                        <span className="truncate">{t('emailLogin')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMethodChange('wechat')}
                        aria-pressed={loginMethod === 'wechat'}
                        className={`login-dialog-method-button flex h-[46px] items-center justify-center gap-2 border-l border-[#E5E7EB] text-[13px] font-medium transition focus:outline-none focus-visible:underline focus-visible:decoration-2 focus-visible:underline-offset-4 ${
                          loginMethod === 'wechat'
                            ? 'bg-[#EDF9FD] text-[#1599CC]'
                            : 'text-[#4F545B] hover:bg-[#F7F8F9] hover:text-[#17191C]'
                        }`}
                      >
                        <QrCode size={15} strokeWidth={1.8} className="shrink-0" />
                        <span className="truncate">{t('wechatQrLogin')}</span>
                      </button>
                    </div>
                  ) : null}

                  {loginMethod === 'wechat' && !showEmailForm ? (
                    <div className="mt-3 flex flex-col items-center">
                      <div className="relative mx-auto aspect-square w-[clamp(180px,calc(100svh-430px),260px)] max-w-full overflow-hidden">
                        {wechatQrUrl ? (
                          <iframe
                            src={wechatQrUrl}
                            title={t('wechatQrAlt')}
                            className={`absolute inset-0 h-full w-full border-0 bg-transparent transition-opacity duration-200 ${wechatQrFrameRevealed && !wechatCallbackReceived && !wechatQrFrameFailed ? 'opacity-100' : 'opacity-0'}`}
                            onLoad={() => {
                              setWechatQrFrameLoaded(true);
                              setWechatQrFrameRevealed(true);
                              setWechatQrFrameFailed(false);
                              setWechatQrLoadSlow(false);
                            }}
                            onError={() => {
                              setWechatQrFrameFailed(true);
                              setWechatQrLoadSlow(true);
                            }}
                          />
                        ) : null}

                        {showWechatQrLoading ? (
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#F3F4F6] px-6 text-center text-[#6B7280]">
                            {!wechatQrFrameFailed ? (
                              <SystemLoadingIcon className="h-8 w-8 animate-spin rounded-full border-2 border-[#CBD5E1] border-t-[#4ABDE9]" />
                            ) : (
                              <QrCode size={30} className="text-[#9CA3AF]" />
                            )}
                            <span className="text-[12px] font-semibold uppercase tracking-[0.18em] text-[#4B5563]">
                              {wechatCallbackReceived ? t('wechatConfirming') : wechatStatus === 'booting' || !wechatQrUrl ? t('wechatPreparing') : wechatQrFrameFailed ? t('wechatQrLoadFailed') : t('wechatLoadingQr')}
                            </span>
                            {wechatQrLoadSlow && !wechatCallbackReceived ? (
                              <span className="max-w-[230px] text-[12px] leading-5 text-[#6B7280]">
                                {t('wechatQrSlow')}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>

                      <div className="mt-3 flex min-h-[38px] flex-col items-center justify-center gap-2 text-center text-[12px] leading-5 text-[#6B7280]">
                        <span>{wechatQrHelperText}</span>
                        {wechatStatus === 'pending' && wechatSecondsRemaining > 0 ? (
                          <span>{t('wechatQrExpiresIn', { seconds: wechatSecondsRemaining })}</span>
                        ) : null}
                        {wechatCallbackReceived && wechatStatus === 'pending' ? (
                          <button
                            type="button"
                            onClick={() => void refreshWechatSessionStatus({ markCallbackReceived: true })}
                            disabled={wechatCheckingStatus}
                            className="inline-flex items-center gap-1.5 rounded-[10px] border border-[#BAE6FD] bg-white px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#0369A1] transition hover:border-[#4ABDE9] disabled:cursor-wait disabled:opacity-60"
                          >
                            <SystemLoadingSwitch icon={RefreshCw} size={12} className={wechatCheckingStatus ? 'animate-spin' : ''} />
                            <span>{wechatCheckingStatus ? t('wechatCheckingStatus') : t('wechatCheckStatus')}</span>
                          </button>
                        ) : null}
                        {showWechatQrInlineRefresh ? (
                          <button
                            type="button"
                            onClick={handleReplaceWechat}
                            disabled={wechatReplacing}
                            className="inline-flex items-center gap-1.5 rounded-[10px] border border-[#D1D5DB] bg-white px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#111827] transition hover:border-[#111827]"
                          >
                            <SystemLoadingSwitch icon={RefreshCw} size={12} className={wechatReplacing ? 'animate-spin' : ''} />
                            <span>{wechatReplacing ? t('wechatReplacingQr') : t('wechatReplaceQr')}</span>
                          </button>
                        ) : null}
                      </div>

                      {wechatStatusMessage ? (
                        <div className={`w-full rounded-[10px] border px-4 py-3 text-[13px] leading-6 ${
                          wechatStatus === 'completed'
                            ? 'border-[#BAE6FD] bg-[#F0F9FF] text-[#0369A1]'
                            : 'border-[#FECACA] bg-[#FEF2F2] text-[#B91C1C]'
                        }`}>
                          {wechatStatusMessage}
                        </div>
                      ) : null}

                      {(wechatStatus === 'failed' || wechatStatus === 'expired') ? (
                        <button
                          type="button"
                          onClick={handleReplaceWechat}
                          disabled={wechatReplacing}
                          className="inline-flex items-center gap-2 rounded-[12px] border border-[#D1D5DB] bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#111827] transition hover:border-[#111827]"
                        >
                          <SystemLoadingSwitch icon={RefreshCw} size={14} className={wechatReplacing ? 'animate-spin' : ''} />
                          <span>{wechatReplacing ? t('wechatReplacingQr') : t('wechatReplaceQr')}</span>
                        </button>
                      ) : null}
                    </div>
                  ) : null}

                  {showPasswordForm ? (
                    <form onSubmit={handleLogin} className="login-dialog-form mx-auto mt-9 w-full max-w-[560px] space-y-6">
                      <div className="space-y-2">
                        <label htmlFor="auth-field-5" className="ml-0.5 text-[14px] font-semibold text-[#3F444B]">
                          {t('email')}
                        </label>
                        <div className="relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-[#9CA3AF]">
                            <Mail size={16} />
                          </div>
                          <input id="auth-field-5"
                            type="email"
                            required
                            autoComplete="email"
                            placeholder={t('emailPlaceholder')}
                            className={`${inputClass} pl-11 pr-4`}
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <label htmlFor="auth-field-6" className="ml-0.5 text-[14px] font-semibold text-[#3F444B]">
                            {t('password')}
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              setError('');
                              setForgotPasswordOpen(true);
                            }}
                            className="text-[12px] font-medium text-[#0284C7] transition hover:text-[#0369A1]"
                          >
                            {t('forgotPassword')}
                          </button>
                        </div>
                        <div className="relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-[#9CA3AF]">
                            <KeyRound size={16} />
                          </div>
                          <input id="auth-field-6"
                            type={passwordVisible ? 'text' : 'password'}
                            required
                            autoComplete="current-password"
                            placeholder={t('passwordPlaceholder')}
                            className={`${inputClass} pl-11 pr-12`}
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                          />
                          <button
                            type="button"
                            onClick={() => setPasswordVisible((current) => !current)}
                            aria-label={passwordVisible ? t('hidePassword') : t('showPassword')}
                            className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-[#6B7280] transition hover:text-[#111827]"
                          >
                            {passwordVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>

                      <StudioAction
                        type="submit"
                        disabled={isLoading}


                        className={`${formStyles.action} w-full gap-2`}
                       variant="primary">
                        {isLoading ? (
                          <SystemLoadingIcon className="h-4 w-4 animate-spin rounded-full border-2 border-[#203644]/20 border-t-[#203644]" />
                        ) : (
                          <>
                            <span>{t('loginWithPassword')}</span>
                            <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                          </>
                        )}
                      </StudioAction>

                      <div className="flex items-center gap-3 pt-1">
                        <div className="h-px flex-1 bg-[#E5E7EB]" />
                        <button
                          type="button"
                          onClick={() => {
                            setError('');
                            setEmailLoginMode('code');
                          }}
                          className="shrink-0 text-[13px] font-medium text-[#1599CC] transition hover:text-[#0369A1]"
                        >
                          {t('useVerificationCode')}
                        </button>
                        <div className="h-px flex-1 bg-[#E5E7EB]" />
                      </div>
                    </form>
                  ) : null}

                  {showEmailCodeForm ? (
                    <form onSubmit={handleLogin} className="login-dialog-form mx-auto mt-9 w-full max-w-[560px] space-y-6">
                      {isWechatAwaitingBind ? (
                        <div className="rounded-[10px] border border-[#BAE6FD] bg-[#F0F9FF] px-4 py-4 text-[#0F172A]">
                          <div className="flex items-start gap-3">
                            <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-[#0284C7]" />
                            <div>
                              <p className="text-[13px] font-semibold">{t('wechatBindReadyTitle')}</p>
                              <p className="mt-1 text-[13px] leading-6 text-[#0369A1]">
                                {t('wechatBindReadyBody')}
                              </p>
                              {wechatIdentityNickname ? (
                                <p className="mt-1 text-[12px] font-medium text-[#075985]">
                                  {t('wechatConfirmedAccount', { nickname: wechatIdentityNickname })}
                                </p>
                              ) : null}
                              <button
                                type="button"
                                onClick={handleReplaceWechat}
                                disabled={wechatReplacing}
                                className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#0369A1] underline decoration-[#7DD3FC] underline-offset-4 transition hover:text-[#075985] disabled:cursor-wait disabled:opacity-60"
                              >
                                <SystemLoadingSwitch icon={RefreshCw} size={12} className={wechatReplacing ? 'animate-spin' : ''} />
                                <span>{wechatReplacing ? t('wechatReplacingQr') : t('wechatReplaceAfterScan')}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : null}
                      {isAppleAwaitingBind ? (
                        <div className="rounded-[10px] border border-[#BAE6FD] bg-[#F0F9FF] px-4 py-4 text-[#0F172A]">
                          <div className="flex items-start gap-3">
                            <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-[#0284C7]" />
                            <div>
                              <p className="text-[13px] font-semibold">{t('appleBindReadyTitle')}</p>
                              <p className="mt-1 text-[13px] leading-6 text-[#0369A1]">
                                {t('appleBindReadyBody')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : null}

                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <label htmlFor="auth-field-7" className="ml-0.5 text-[14px] font-semibold text-[#3F444B]">
                          {t('email')}
                        </label>
                      </div>
                      <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-[#9CA3AF]">
                          <Mail size={16} />
                        </div>
                        <input id="auth-field-7"
                          type="email"
                          required
                          autoComplete="email"
                          placeholder={t('emailPlaceholder')}
                          className={`${inputClass} pl-11 pr-4`}
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="auth-field-8" className="ml-0.5 text-[14px] font-semibold text-[#3F444B]">
                        {t('verificationCode')}
                      </label>
                      <div className="relative">
                        <div className="relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-[#9CA3AF]">
                            <KeyRound size={16} />
                          </div>
                          <input id="auth-field-8"
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            required
                            placeholder={t('codePlaceholder')}
                            className={`${inputClass} pl-11 pr-[118px] font-medium tracking-[0.08em] tabular-nums`}
                            value={verificationCode}
                            onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                          />
                        </div>
                        <StudioAction variant="secondary" size="compact"
                          type="button"
                          onClick={handleSendCode}
                          disabled={!email || isSendingCode || countdown > 0}
                          className="absolute inset-y-1 right-1 !min-h-0 w-28 !px-2 text-[12px]"
                        >
                          {isSendingCode ? (
                            <SystemLoadingIcon className="h-4 w-4 animate-spin rounded-full border-2 border-[#203644]/20 border-t-[#203644]" />
                          ) : countdown > 0 ? (
                            <span className="tabular-nums normal-case tracking-[0.02em]">{countdown}s</span>
                          ) : codeSent ? (
                            t('resend')
                          ) : (
                            t('sendCode')
                          )}
                        </StudioAction>
                      </div>
                    </div>

                    <StudioAction
                      type="submit"
                      disabled={isLoading}


                      className={`${formStyles.action} w-full gap-2`}
                     variant="primary">
                      {isLoading ? (
                        <SystemLoadingIcon className="h-4 w-4 animate-spin rounded-full border-2 border-[#203644]/20 border-t-[#203644]" />
                      ) : (
                        <>
                          <span>{isWechatAwaitingBind ? t('wechatCompleteBinding') : isAppleAwaitingBind ? t('appleCompleteBinding') : t('login')}</span>
                          <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                        </>
                      )}
                    </StudioAction>

                    <div className="flex items-center gap-3 pt-1">
                      <div className="h-px flex-1 bg-[#E5E7EB]" />
                      <button
                        type="button"
                        onClick={() => {
                          setError('');
                          setEmailLoginMode('password');
                        }}
                        className="shrink-0 text-[13px] font-medium text-[#1599CC] transition hover:text-[#0369A1]"
                      >
                        {t('usePassword')}
                      </button>
                      <div className="h-px flex-1 bg-[#E5E7EB]" />
                    </div>

                    {SHOW_REFERRAL_CONTROLS && !storedAttribution && (
                      <div className="pt-0.5">
                        {referralInputOpen || referralCode ? (
                          <div className="relative w-full sm:max-w-[320px]">
                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-[#9CA3AF]">
                              <Gift size={14} />
                            </div>
                            <input
                              id="login-referral-code"
                              type="text"
                              autoComplete="off"
                              inputMode="text"
                              maxLength={32}
                              placeholder={t('referralCodePlaceholder')}
                              className={`${inviteInputClass} pl-10 pr-4 uppercase`}
                              value={referralCode}
                              onChange={(event) => setReferralCode(event.target.value.toUpperCase().replace(/\s+/g, ''))}
                            />
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setReferralInputOpen(true)}
                            className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#6B7280] transition hover:text-[#111827]"
                          >
                            <Gift size={13} />
                            <span>{t('referralCodeLabel')}</span>
                          </button>
                        )}
                      </div>
                    )}

                    </form>
                  ) : null}

                  {showEmailForm ? (
                    <div className="mt-6">
                      <label className="flex cursor-pointer items-start gap-3 text-[12px] leading-[17px] text-[#50697A]">
                        <input
                          ref={consentCheckboxRef}
                          type="checkbox"
                          checked={consentChecked}
                          aria-invalid={consentError}
                          aria-describedby={consentError ? 'login-consent-help' : undefined}
                          onChange={(event) => {
                            setConsentChecked(event.target.checked);
                            if (event.target.checked) {
                              setConsentError(false);
                              setError('');
                            }
                          }}
                          className="mt-0.5 h-[18px] w-[18px] shrink-0 cursor-pointer accent-[#4ABDE9]"
                        />
                        <span>
                          {t.rich('loginConsentLabel', {
                            terms: (chunks) => <Link href="/terms" target="_blank" className="font-semibold text-[#167BA4] underline underline-offset-2" onClick={(event) => event.stopPropagation()}>{chunks}</Link>,
                            privacy: (chunks) => <Link href="/privacy" target="_blank" className="font-semibold text-[#167BA4] underline underline-offset-2" onClick={(event) => event.stopPropagation()}>{chunks}</Link>,
                          })}
                        </span>
                      </label>
                      {consentError ? (
                        <p id="login-consent-help" aria-live="polite" className="pl-[30px] text-[12px] leading-5 text-[#577185]">
                          {t('loginConsentRequired')}
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  {loginEntryMode === 'email' && !isProviderAwaitingBind ? (
                    <button
                      type="button"
                      onClick={() => {
                        setError('');
                        setAppleBindTicket('');
                        setLoginEntryMode('choices');
                        setEmailLoginMode('code');
                      }}
                      className="mx-auto mt-4 inline-flex items-center gap-1.5 text-[12px] font-medium text-[#6B7280] transition hover:text-[#111827]"
                    >
                      <ArrowLeft size={14} />
                      <span>{t('otherSignInMethods')}</span>
                    </button>
                  ) : null}
                    </>
                  )}
                </div>
            </div>
          </motion.div>
        </motion.div>
        ) : null}
      </AnimatePresence>
      <style jsx global>{`
        .login-dialog-content {
          scrollbar-width: thin;
          scrollbar-color: rgba(74,189,233,0.35) transparent;
        }
        .login-dialog-content input:not([type='checkbox']) {
          border-color: #D5EAF4 !important;
          background: #FFFFFF !important;
          color: #173349 !important;
        }
        .login-dialog-content input:not([type='checkbox'])::placeholder {
          color: #91A3AE !important;
        }
        .login-dialog-content input:not([type='checkbox']):focus {
          border-color: #4ABDE9 !important;
          box-shadow: 0 0 0 4px rgba(74,189,233,0.12);
        }
        .login-dialog-content label:not(:has(input)),
        .login-dialog-content h2 {
          color: #173349 !important;
        }
        @media (max-height: 680px) {
          .login-dialog-header {
            margin-top: 8px;
          }

          .login-dialog-title {
            font-size: 24px;
          }

          .login-dialog-subtitle {
            font-size: 13px;
            line-height: 16px;
          }

          .login-dialog-method-tabs {
            margin-top: 12px;
          }

          .login-dialog-method-button {
            height: 36px;
          }

          .login-dialog-form {
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-top: 12px;
          }

          .login-dialog-form > :not([hidden]) ~ :not([hidden]) {
            margin-top: 0;
            margin-bottom: 0;
          }
        }
      `}</style>
    </>
  );
}

